/**
 * Water Plan + deterministic water assessment. The plan captures supply context
 * (hydrant distance/confidence, static water); the assessment combines it with
 * the responding units' onboard water to decide whether supply looks limited.
 */

import type { Confidence } from './fact.js';
import type { DutyConfig } from './config.js';
import type { ResourceUnit } from './resources.js';
import { summariseCapability } from './resources.js';

export interface WaterPlan {
  /** Nearest mapped hydrant distance in metres (from seed data or manual). */
  nearestHydrantDistanceM?: number;
  hydrantConfidence?: Confidence;
  staticWaterAvailable?: boolean;
  staticWaterNote?: string;
  relayOrShuttle?: boolean;
}

export function emptyWaterPlan(): WaterPlan {
  return {};
}

export interface WaterAssessment {
  /** undefined when hydrant distance is unknown. */
  hydrantInRange: boolean | undefined;
  hydrantKnown: boolean;
  bulkWaterAssigned: boolean;
  onboardWaterL: number;
  staticWaterAvailable: boolean;
  /** True when sustained structural supply looks limited. */
  limited: boolean;
}

/**
 * Assess water supply. Limited when there's no bulk water assigned AND the
 * hydrant is out of range (or unknown) AND committed onboard water is below the
 * configured sizing figure. Pure.
 */
export function assessWater(
  plan: WaterPlan,
  units: readonly ResourceUnit[],
  config: DutyConfig,
): WaterAssessment {
  const cap = summariseCapability(units, config.water.bulkWaterMinL);
  const hydrantKnown = typeof plan.nearestHydrantDistanceM === 'number';
  const hydrantInRange = hydrantKnown
    ? plan.nearestHydrantDistanceM! <= config.water.hydrantConcernDistanceM
    : undefined;
  const onboardLow = cap.onboardWaterL < config.water.limitedOnboardL;

  const limited =
    !cap.hasBulkWater && hydrantInRange !== true && (onboardLow || !hydrantKnown);

  return {
    hydrantInRange,
    hydrantKnown,
    bulkWaterAssigned: cap.hasBulkWater,
    onboardWaterL: cap.onboardWaterL,
    staticWaterAvailable: plan.staticWaterAvailable === true,
    limited,
  };
}
